import { useUpdateNodeInternals } from '@xyflow/react'
import { useEffect, useMemo } from 'react'
import { agentLogger } from 'v2/utils/logger'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowRunStore } from '../store'
import type { NodeExecutedEvent } from '../types'
import { highlightNodeAndEdges, highlightSingleNodeAndEdge } from '../utils'

type HighlightMode = 'current-only' | 'full-path'

interface ExecutionPathData {
  executedNodeIds: string[]
  chosenEdges: Array<{
    sourceNodeId: string
    targetNodeId: string
    sourceHandle: string
    chosenPath: string
  }>
  currentlyExecutingNodeId: string | null
}

export const usePathHighlighter = (mode: HighlightMode = 'full-path', dependency?: unknown) => {
  const { executionHistory } = useFlowRunStore()
  const { fitView, getNode } = useTypedReactFlow()
  const updateInternals = useUpdateNodeInternals()

  // Get the currently executing node (last node in execution history)
  const currentlyExecutingNode = executionHistory.at(-1)

  const { current_node_uuid: currentNodeUuid } = currentlyExecutingNode || {}

  // Build execution path data from history
  const executionPathData = useMemo((): ExecutionPathData => {
    const executedNodeIds: string[] = []
    const chosenEdges: ExecutionPathData['chosenEdges'] = []

    executionHistory.forEach((event: NodeExecutedEvent) => {
      // Add executed node
      if (event.current_node_uuid) {
        executedNodeIds.push(event.current_node_uuid)
      }

      // Add chosen edge if we have both source and target
      if (event.previous_node_uuid && event.current_node_uuid && event.chosen_path) {
        chosenEdges.push({
          sourceNodeId: event.previous_node_uuid,
          targetNodeId: event.current_node_uuid,
          sourceHandle: event.chosen_path,
          chosenPath: event.chosen_path,
        })
      }
    })

    return {
      executedNodeIds: [...new Set(executedNodeIds)], // Remove duplicates
      chosenEdges,
      currentlyExecutingNodeId: currentNodeUuid || null,
    }
  }, [executionHistory, currentNodeUuid])

  useEffect(() => {
    if (executionHistory.length === 0) return

    let toCanvasId: (wireNodeId: string) => string
    if (mode === 'current-only') {
      // Only highlight the current executing node and its immediate edge
      const {
        current_node_uuid: currentNodeId,
        chosen_path: chosenPath,
        previous_node_uuid: previousNodeUuid,
      } = currentlyExecutingNode || {}
      toCanvasId = highlightSingleNodeAndEdge(currentNodeId, chosenPath, previousNodeUuid)
    } else {
      // Highlight the full execution path
      toCanvasId = highlightNodeAndEdges(
        executionPathData.executedNodeIds,
        executionPathData.chosenEdges,
      )
    }

    // Center the currently executing node if available. fitView on an id the canvas
    // doesn't hold pans to empty space at the origin.
    if (currentNodeUuid && mode === 'current-only') {
      const canvasNodeId = toCanvasId(currentNodeUuid)
      if (getNode(canvasNodeId)) {
        fitView({
          nodes: [{ id: canvasNodeId }],
          duration: 300,
          maxZoom: 0.9,
        })
        updateInternals(canvasNodeId)
      }
    }

    agentLogger.debug('executionPathData', executionPathData)
  }, [mode, executionHistory, dependency])

  return {
    currentlyExecutingNodeId: currentlyExecutingNode?.current_node_uuid || null,
    executionHistory,
    executionPathData,
    isNodeCurrentlyExecuting: (nodeId: string) =>
      nodeId === currentlyExecutingNode?.current_node_uuid,
    shouldHighlightNode: (nodeId: string) => executionPathData.executedNodeIds.includes(nodeId),
    isEdgeInExecutionPath: (sourceId: string, targetId: string) =>
      executionPathData.chosenEdges.some(
        (edge) => edge.sourceNodeId === sourceId && edge.targetNodeId === targetId,
      ),
  }
}
